import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { UserContext } from '../../UserContext'; // ✅ Import User Context
import '../../index.css'; // Import the CSS file directly

const Stress = () => {
  const { user } = useContext(UserContext); // ✅ Get user from context
  const category = 'stress'; // ✅ Category for fetching videos
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`https://wholeness-forward.onrender.com/api/video/videos/${category}`);
        if (Array.isArray(response.data)) {
          setVideos(response.data);
        }
      } catch (error) {
        console.error('Error fetching videos:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchVideos();
  }, []);

  // ✅ Handle Mark as Helpful for logged-in users only
  const handleMarkHelpful = async (videoId) => {
    if (!user || !user.id) {
      alert('You must be logged in to mark as helpful.');
      return;
    }

    try {
      const response = await axios.post(`https://wholeness-forward.onrender.com/api/video/videos/toggleHelpful`, {
        videoId,
        userId: user.id,
      });

      // ✅ Update UI with new helpful count
      setVideos((prevVideos) =>
        prevVideos.map((video) =>
          video._id === videoId
            ? { ...video, helpfulCount: response.data.helpfulCount }
            : video
        )
      );
    } catch (error) {
      console.error('Error marking video as helpful:', error);
    }
  };

  return (
    <div className="page-container stress-page">
      <h1 className="page-title">Managing Stress</h1>

      {/* Include the image */}
      <img
        src="/images/wf-managingstress.webp"
        alt="Managing Stress"
        className="page-image"
      />

      <p className="page-text">Welcome to the Managing Stress section of Wholeness Forward.</p>

      <p className="page-text">
        Stress is a natural response to life's challenges, but learning to manage it effectively can greatly enhance your mental and physical well-being. 
        By understanding the mechanisms of stress and applying practical techniques, you can cultivate resilience and thrive under pressure.
      </p>
      <p className="page-text">
        In this section, we explore science-backed strategies and habits to reduce stress, including mindfulness, physical activity, and fostering a balanced mindset.
      </p>

      <hr className="page-divider" />

      <h2 className="page-heading">Health Benefits of Stress Management</h2>
      <p className="page-text">
        Effectively managing stress can provide numerous health benefits:
      </p>
      <ul className="content-list">
        <li><strong>Improved Mental Health:</strong> Reduces symptoms of anxiety, depression, and mental fatigue.</li>
        <li><strong>Better Sleep Quality:</strong> Helps combat insomnia and promotes deeper, more restful sleep.</li>
        <li><strong>Enhanced Immune Function:</strong> Lower stress levels reduce the risk of illness and improve immune responses.</li>
        <li><strong>Increased Resilience:</strong> Building coping mechanisms strengthens emotional resilience and improves your ability to handle life challenges.</li>
        <li><strong>Improved Relationships:</strong> Stress management fosters better communication and empathy in personal and professional relationships.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Strategies for Managing Stress</h2>
      <ul className="content-list">
        <li><strong>Mindfulness and Meditation:</strong> Practice deep breathing and meditation to calm the mind and body.</li>
        <li><strong>Physical Activity:</strong> Regular exercise, such as walking, yoga, or running, can help release built-up tension.</li>
        <li><strong>Time Management:</strong> Organize your schedule to reduce feelings of overwhelm and ensure you have time for relaxation.</li>
        <li><strong>Social Support:</strong> Engage with friends, family, or professional counselors to seek support and advice during stressful times.</li>
        <li><strong>Relaxation Techniques:</strong> Practices like progressive muscle relaxation, guided imagery, and aromatherapy can ease stress.</li>
      </ul>

      <h3 className="subheading">Starting Your Stress Management Practice</h3>
      <ul className="content-list">
        <li><strong>Start Small:</strong> Begin with short mindfulness exercises or simple breathing techniques, and gradually extend the duration.</li>
        <li><strong>Be Consistent:</strong> Consistency is key. Try to incorporate stress-relief techniques into your daily life, even on busy days.</li>
        <li><strong>Track Your Progress:</strong> Keep a journal to track your stress levels and the effectiveness of different techniques over time.</li>
        <li><strong>Practice Self-Compassion:</strong> Be gentle with yourself. Stress management is a journey, not a quick fix.</li>
      </ul>

      <hr className="page-divider" />

      <h2 className="page-heading">Curated Video Collection</h2>
      <p className="page-text">
        Watch these videos to deepen your understanding of stress management techniques and their benefits:
      </p>

      {loading ? (
        <p className="message">Loading videos...</p>
      ) : (
        <div className="video-container">
          {videos.length > 0 ? (
            videos.map((video) => (
              <div key={video._id} className="video-card">
                <h3 className="video-title">{video.title}</h3>
                <p className="video-details">
                  <strong>Source:</strong> {video.source} <br />
                  <strong>Length:</strong> {video.length} <br />
                  {video.description}
                </p>
                <iframe
                  className="video-frame"
                  src={video.url}
                  title={video.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                ></iframe>
                <div className="video-actions">
                  {/* ✅ Helpful Button */}
                  <button
                    className="button"
                    onClick={() => handleMarkHelpful(video._id)}
                    disabled={video.usersWhoClickedHelpful?.includes(user?.id)}
                  >
                    {video.usersWhoClickedHelpful?.includes(user?.id)
                      ? `Helpful (${video.helpfulCount})`
                      : `Mark as Helpful (${video.helpfulCount || 0})`}
                  </button>
                </div>
                <hr />
              </div>
            ))
          ) : (
            <p className="no-videos-message">No videos found for this category.</p>
          )}
        </div>
      )}


      <hr className="page-divider" />

      <h3 className="subheading">Join the Stress Management Community</h3>
      <p className="page-text">
        Share your experiences, ask questions, and gain insights from others on their journey to better stress management. 
        Whether you're looking to build healthier habits or simply want to feel calmer, this is the place to connect and grow.
      </p>
      <a href="/forum/Stress_Management" className="btn">Join the Forum</a>
    </div>
  );
};

export default Stress;
